<template>
  <div class="modern-master-view pb-12">
    <v-row class="mb-8 align-center px-4 pt-4">
      <div class="d-flex align-center">
        <v-avatar color="primary" size="48" class="mr-4 shadow-sm">
          <v-icon color="white">fas fa-filter</v-icon>
        </v-avatar>
        <div>
          <div class="text-h4 font-weight-black primary--text lh-1">Pipeline CRM</div>
          <div class="caption grey--text font-weight-bold uppercase tracking-widest mt-1">
            Oportunidades antes de cotizar · vendedor y jefatura
          </div>
        </div>
      </div>
      <v-spacer></v-spacer>
      <v-btn outlined color="primary" class="font-weight-black text-uppercase tracking-widest rounded-lg px-6 mr-2" :loading="loading" @click="fetchAll">
        <v-icon left small>fas fa-redo</v-icon> Actualizar
      </v-btn>
      <v-btn color="primary" class="font-weight-black text-uppercase tracking-widest rounded-lg px-6" @click="openCreate">
        <v-icon left small>fas fa-plus</v-icon> Nueva oportunidad
      </v-btn>
    </v-row>

    <v-card outlined class="modern-card pa-6 mb-6 mx-4">
      <v-row dense align="center">
        <v-col cols="12" md="4">
          <v-text-field v-model="search" outlined dense hide-details label="Buscar" class="modern-input" clearable @keydown.enter="fetchAll" />
        </v-col>
        <v-col v-if="canSeeAll" cols="12" md="4">
          <v-select v-model="sellerId" :items="sellerItems" item-text="text" item-value="value" outlined dense hide-details clearable label="Vendedor" class="modern-input" @change="fetchAll" />
        </v-col>
        <v-col cols="12" md="4" class="text-right">
          <v-btn-toggle v-model="tab" mandatory dense>
            <v-btn small>Tablero</v-btn>
            <v-btn small>Pendientes</v-btn>
            <v-btn small>Realizadas</v-btn>
          </v-btn-toggle>
        </v-col>
      </v-row>
    </v-card>

    <v-row class="mb-6 px-4">
      <v-col cols="12" sm="3">
        <v-card outlined class="modern-card pa-6">
          <div class="caption font-weight-black text-uppercase tracking-widest grey--text mb-1">Abiertas</div>
          <div class="text-h5 font-weight-black">{{ kpis.open_count || 0 }}</div>
          <div class="caption grey--text">{{ kpis.open_amount | moneda }}</div>
        </v-card>
      </v-col>
      <v-col cols="12" sm="3">
        <v-card outlined class="modern-card pa-6">
          <div class="caption font-weight-black text-uppercase tracking-widest grey--text mb-1">Seguimiento vencido</div>
          <div class="text-h5 font-weight-black error--text">{{ kpis.overdue_count || 0 }}</div>
        </v-card>
      </v-col>
      <v-col cols="12" sm="3">
        <v-card outlined class="modern-card pa-6">
          <div class="caption font-weight-black text-uppercase tracking-widest grey--text mb-1">Tareas pendientes</div>
          <div class="text-h5 font-weight-black">{{ (tasks.activities || []).length }}</div>
        </v-card>
      </v-col>
      <v-col cols="12" sm="3">
        <v-card outlined class="modern-card pa-6">
          <div class="caption font-weight-black text-uppercase tracking-widest grey--text mb-1">Tareas realizadas</div>
          <div class="text-h5 font-weight-black">{{ (tasks.done_activities || []).length }}</div>
        </v-card>
      </v-col>
    </v-row>

    <div v-if="tab === 0" class="px-4" style="overflow-x: auto;">
      <div class="d-flex" style="gap: 12px; min-height: 420px;">
        <v-card v-for="col in columns" :key="col.key" outlined class="modern-card flex-grow-0" style="width: 280px; min-width: 280px;">
          <div class="pa-4 pb-2">
            <div class="subtitle-2 font-weight-black">{{ col.label }}</div>
            <div class="caption grey--text">{{ col.count }} · {{ col.amount | moneda }}</div>
          </div>
          <draggable :list="col.items" group="crm" :animation="150" class="pa-2" style="min-height: 320px;" @change="onDrop(col.key, $event)">
            <v-card
              v-for="item in col.items"
              :key="item.id"
              outlined
              class="mb-2 pa-3"
              style="cursor: pointer;"
              @click="openItem(item)"
            >
              <div class="font-weight-bold">{{ item.title }}</div>
              <div class="caption grey--text">{{ item.account_name }}</div>
              <div class="font-weight-black mt-1">{{ item.amount | moneda }}</div>
              <div class="caption" :class="item.overdue ? 'error--text' : 'grey--text'">
                {{ item.seller_name }}
                <span v-if="item.next_action_at"> · {{ item.next_action_at }}</span>
              </div>
            </v-card>
          </draggable>
        </v-card>
      </div>
    </div>

    <v-card v-else-if="tab === 1" outlined class="modern-card mx-4">
      <v-data-table :headers="taskHeaders" :items="taskRows" :loading="loading" hide-default-footer class="elevation-0" no-data-text="No hay tareas abiertas.">
        <template v-slot:item.amount="{ item }">{{ item.amount | moneda }}</template>
        <template v-slot:item.overdue="{ item }">
          <v-chip v-if="item.overdue" x-small color="error" class="white--text">Vencida</v-chip>
        </template>
        <template v-slot:item.actions="{ item }">
          <v-btn text small color="primary" @click="openItem(item)">Ver</v-btn>
          <v-btn v-if="item.activity_id" text small @click="completeTask(item)">Hecha</v-btn>
        </template>
      </v-data-table>
    </v-card>

    <v-card v-else outlined class="modern-card mx-4">
      <div class="pa-4 pb-0">
        <div class="subtitle-2 font-weight-black mb-1">Detalle de tareas realizadas</div>
        <div class="caption grey--text mb-4">Llamados, visitas y tareas ya marcadas como hechas.</div>
        <v-row dense class="mb-2">
          <v-col cols="12" sm="4" md="3">
            <v-text-field v-model="doneFrom" type="date" outlined dense hide-details label="Desde" @change="fetchAll" />
          </v-col>
          <v-col cols="12" sm="4" md="3">
            <v-text-field v-model="doneTo" type="date" outlined dense hide-details label="Hasta" @change="fetchAll" />
          </v-col>
        </v-row>
      </div>
      <v-data-table
        :headers="doneHeaders"
        :items="doneRows"
        :loading="loading"
        :items-per-page="20"
        class="elevation-0"
        no-data-text="Aún no hay tareas realizadas en este período."
      >
        <template v-slot:item.done_at="{ item }">{{ formatDate(item.done_at) }}</template>
        <template v-slot:item.due_date="{ item }">{{ formatDate(item.due_date) }}</template>
        <template v-slot:item.actions="{ item }">
          <v-btn text small color="primary" @click="openItem({ id: item.opportunity_id })">Ver</v-btn>
        </template>
      </v-data-table>
    </v-card>

    <v-dialog v-model="dialog" max-width="560">
      <v-card>
        <v-card-title class="font-weight-black">Nueva oportunidad</v-card-title>
        <v-card-text>
          <v-text-field v-model="form.title" label="Título" outlined dense />
          <v-autocomplete
            v-model="form.customer_id"
            :items="customerItems"
            :search-input.sync="customerSearch"
            item-text="name"
            item-value="id"
            label="Cliente existente (opcional)"
            outlined
            dense
            clearable
            hide-no-data
            @change="onCustomerPicked"
          />
          <v-text-field v-model="form.account_name" label="Nombre de cuenta / prospecto" outlined dense />
          <v-text-field v-model="form.contact_name" label="Contacto" outlined dense />
          <v-text-field v-model="form.phone" label="Teléfono" outlined dense />
          <v-text-field v-model="form.amount" type="number" label="Monto estimado" outlined dense />
          <v-text-field v-model="form.next_action_at" type="date" label="Próximo contacto" outlined dense />
          <v-text-field v-model="form.next_action_note" label="Qué hay que hacer" outlined dense />
          <v-select v-if="canSeeAll" v-model="form.seller_id" :items="sellerItems" item-text="text" item-value="value" label="Vendedor" outlined dense />
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="dialog = false">Cancelar</v-btn>
          <v-btn color="primary" :loading="saving" @click="submitCreate">Crear</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="lostDialog" max-width="420">
      <v-card>
        <v-card-title class="font-weight-black">Marcar perdida</v-card-title>
        <v-card-text>
          <v-text-field v-model="lostReason" label="Motivo" outlined dense />
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="lostDialog = false">Cancelar</v-btn>
          <v-btn color="error" :loading="saving" @click="confirmLost">Perdida</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import draggable from 'vuedraggable'
import notifications from '@/utils/notifications'
import { fetchCustomers } from '@/api/customers'
import {
  completeCrmActivity,
  createCrmOpportunity,
  fetchCrmPipeline,
  fetchCrmTasks,
  setCrmOpportunityStage,
} from '@/api/crm'

export default {
  name: 'CrmPipeline',
  components: { draggable },
  data() {
    return {
      loading: false,
      saving: false,
      tab: 0,
      search: '',
      sellerId: null,
      kpis: {},
      columns: [],
      canSeeAll: false,
      tasks: { next_actions: [], activities: [], done_activities: [] },
      doneFrom: '',
      doneTo: '',
      dialog: false,
      lostDialog: false,
      lostReason: '',
      pendingLostId: null,
      customerSearch: '',
      customerItems: [],
      form: this.emptyForm(),
      taskHeaders: [
        { text: 'Cuenta', value: 'account_name' },
        { text: 'Oportunidad', value: 'title' },
        { text: 'Fecha', value: 'due' },
        { text: 'Monto', value: 'amount', align: 'end' },
        { text: '', value: 'overdue' },
        { text: '', value: 'actions', sortable: false },
      ],
      doneHeaders: [
        { text: 'Hecha', value: 'done_at' },
        { text: 'Tipo', value: 'tipo_label' },
        { text: 'Cuenta', value: 'account_name' },
        { text: 'Oportunidad', value: 'opportunity_title' },
        { text: 'Detalle', value: 'notes' },
        { text: 'Vendedor', value: 'seller_name' },
        { text: 'Comprometida', value: 'due_date' },
        { text: '', value: 'actions', sortable: false },
      ],
    }
  },
  computed: {
    sellerItems() {
      const raw = this.$store.state.sellers
      const list = Array.isArray(raw) ? raw : (raw && raw.results) || []
      const items = list.map((s) => ({
        value: s.id,
        text: `${s.first_name || ''} ${s.last_name || ''}`.trim() || s.email,
      }))
      const user = this.$store.state.user
      if (user && user.id && !items.some((item) => item.value === user.id)) {
        items.unshift({
          value: user.id,
          text: `${user.first_name || ''} ${user.last_name || ''}`.trim() || user.email || 'Yo',
        })
      }
      return items
    },
    taskRows() {
      const nexts = (this.tasks.next_actions || []).map((item) => ({
        ...item,
        due: item.next_action_at,
        activity_id: null,
      }))
      const acts = (this.tasks.activities || []).map((item) => ({
        id: item.opportunity_id,
        title: item.opportunity_title,
        account_name: item.account_name,
        amount: 0,
        due: item.due_date,
        overdue: item.overdue,
        activity_id: item.id,
      }))
      return nexts.concat(acts)
    },
    doneRows() {
      return this.tasks.done_activities || []
    },
  },
  watch: {
    customerSearch(val) {
      if (val) this.searchCustomers(val)
    },
  },
  methods: {
    emptyForm() {
      const user = this.$store.state.user || {}
      return {
        title: '',
        customer_id: null,
        account_name: '',
        contact_name: '',
        phone: '',
        amount: 0,
        next_action_at: '',
        next_action_note: '',
        seller_id: user.id || null,
      }
    },
    openItem(item) {
      if (item && item.id) this.$router.push({ name: 'CrmOpportunity', params: { id: item.id } })
    },
    openCreate() {
      this.form = this.emptyForm()
      this.dialog = true
    },
    onCustomerPicked(id) {
      const found = this.customerItems.find((c) => c.id === id)
      if (found && !this.form.account_name) this.form.account_name = found.name
    },
    async searchCustomers(query) {
      try {
        const data = await fetchCustomers('', 1, 20, query)
        this.customerItems = data.results || data || []
      } catch (e) {
        this.customerItems = []
      }
    },
    async fetchAll() {
      this.loading = true
      try {
        const params = {
          search: this.search || undefined,
          seller_id: this.sellerId || undefined,
          date_after: this.doneFrom || undefined,
          date_before: this.doneTo || undefined,
        }
        const [board, tasks] = await Promise.all([fetchCrmPipeline(params), fetchCrmTasks(params)])
        this.columns = board.columns || []
        this.kpis = board.kpis || {}
        this.canSeeAll = !!board.can_see_all
        this.tasks = tasks || { next_actions: [], activities: [], done_activities: [] }
      } catch (e) {
        notifications.showError('No se pudo cargar el pipeline')
      } finally {
        this.loading = false
      }
    },
    async onDrop(stage, evt) {
      if (!evt || !evt.added) return
      const item = evt.added.element
      if (stage === 'lost') {
        this.pendingLostId = item.id
        this.lostReason = ''
        this.lostDialog = true
        await this.fetchAll()
        return
      }
      try {
        await setCrmOpportunityStage(item.id, { stage })
      } catch (e) {
        notifications.showError('No se pudo mover la oportunidad')
      }
      await this.fetchAll()
    },
    async confirmLost() {
      if (!this.pendingLostId) return
      this.saving = true
      try {
        await setCrmOpportunityStage(this.pendingLostId, { stage: 'lost', lost_reason: this.lostReason })
        this.lostDialog = false
        this.pendingLostId = null
        await this.fetchAll()
      } catch (e) {
        notifications.showError('No se pudo marcar como perdida')
      } finally {
        this.saving = false
      }
    },
    async submitCreate() {
      this.saving = true
      try {
        const payload = {
          title: this.form.title,
          customer_id: this.form.customer_id || null,
          account_name: this.form.account_name,
          contact_name: this.form.contact_name,
          phone: this.form.phone,
          amount: this.form.amount === '' || this.form.amount == null ? 0 : this.form.amount,
          next_action_at: this.form.next_action_at || null,
          next_action_note: this.form.next_action_note,
        }
        if (this.canSeeAll && this.form.seller_id) payload.seller_id = this.form.seller_id
        const created = await createCrmOpportunity(payload)
        this.dialog = false
        notifications.showSuccess('Oportunidad creada')
        this.$router.push({ name: 'CrmOpportunity', params: { id: created.id } })
      } catch (e) {
        const msg = (e.response && e.response.data && e.response.data.error) || 'No se pudo crear'
        notifications.showError(msg)
      } finally {
        this.saving = false
      }
    },
    formatDate(value) {
      if (!value) return '—'
      return String(value).slice(0, 10)
    },
    async completeTask(item) {
      try {
        await completeCrmActivity(item.activity_id)
        await this.fetchAll()
      } catch (e) {
        notifications.showError('No se pudo completar la tarea')
      }
    },
  },
  created() {
    this.fetchAll()
  },
}
</script>
